import { useState, useEffect } from 'react';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { Card, CardContent, CircularProgress, Typography, Box, Divider } from '@mui/material';

// Hooks
import useAuthScope from 'hooks/useAuthScope';
import useDataSourceManager from 'hooks/useDataSourceManager';

// Components
import DrawerField from 'components/Drawer';
import GlobalAlert from 'components/Notification';

import CustomTooltip from './components/CustomTooltip';
import SuccessRateBarChart from './components/SuccessRateBarChart';
import RoamingSuccessRateInfo from './components/RoamingSuccessRateInfo';

// Const
import { defaultSuccessRateInfo } from './const';

import icn_linebar_chart from 'assets/images/icons/icn_linebar_chart.svg';

const Roam = () => {
  const { auth, standalone, isAuthReady } = useAuthScope();
  const { sendBatch } = useDataSourceManager();
  const [roamSuccessRateUuid, _setRoamSuccessRateUuid] = useState(uuidv4());

  const [successRateInfo, setSuccessRateInfo] = useState(defaultSuccessRateInfo);
  const [successRateNoData, setSuccessRateNoData] = useState(true);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: '',
    message: '',
  });

  const fetchSuccessRate = () => {
    const apiUrl = standalone
      ? `/orgs/:orgId/sites/:siteId/roam/successRate.read`
      : `/ents/:entId/orgs/:orgId/sites/:siteId/roam/successRate.read`;

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };

    const mode = standalone ? 'standalone' : 'nonStandalone';
    const restfulParams = restfulParamsConfig[mode];

    return {
      apiKey: apiUrl,
      restfulPlaceholders: restfulParams,
      params: {},
    };
  };

  const formatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short', // Use unit abbreviations (e.g., k, M, B)
    maximumFractionDigits: 1,
  });

  const formatTime = ms => {
    const num = parseInt(ms, 10);
    return num >= 1000 ? `${num / 1000}s` : `${num}ms`;
  };

  const parseSuccessRateInfo = successRateData => {
    const { pingPongCount = 0, P95ms = '-', threshold = 0, total = '-', success = '-', series = [] } = successRateData || {};

    let statusLabel = '-';

    const slowRoamRatio = total === 0 ? 0 : (total - success) / total;
    const pingpongRatio = pingPongCount / total;

    if (P95ms <= 2000 && slowRoamRatio <= 0.05) {
      statusLabel = 'Good';
    } else if ((P95ms > 2000 && P95ms <= 4000) || (slowRoamRatio > 0.05 && slowRoamRatio <= 0.15) || (pingpongRatio > 0.01 && pingpongRatio <= 0.03)) {
      statusLabel = 'Warning';
    } else if (P95ms > 4000 || slowRoamRatio > 0.15 || pingpongRatio > 0.03) {
      statusLabel = 'Critical';
    } else {
      statusLabel = '-';
    }

    const totalEventsLabel = formatter.format(total);

    const formattedSeries = series.map(([range, value]) => {
      let newRange = range;

      if (range.endsWith('-')) {
        // Handle "2000-" -> "> 2s"
        newRange = `> ${formatTime(range)}`;
      } else {
        // Handle "200-2000" -> "200ms-2s"
        const [start, end] = range.split('-');
        newRange = `${formatTime(start)}-${formatTime(end)}`;
      }

      return [newRange, value];
    });

    const hasValidSuccess = typeof success === 'number' && success >= 0;
    const hasValidTotal = typeof total === 'number' && total >= 0;
    const successRateLabel = hasValidSuccess && hasValidTotal && total > 0 ? `${((success / total) * 100).toFixed(0)}%` : '0%';

    setSuccessRateInfo({
      status: typeof P95ms === 'number' && P95ms >= 0 ? statusLabel : '-',
      successRate: hasValidSuccess && hasValidTotal ? successRateLabel : '-',
      totalEvents: hasValidTotal ? totalEventsLabel : '-',
      p95Latency: typeof P95ms === 'number' && P95ms >= 0 ? `${(P95ms / 1000).toFixed(1)}s` : '-',
      series: formattedSeries,
    });
  };

  const checkUiDisplayNoData = (successRateRes) => {
    const isApCountZero = Number(successRateRes?.data?.apCount) === 0;
    // const isAllZero = successRateRes?.data?.series?.every(([_, value]) => value === 0);

    return isApCountZero;
  }

  const init = async () => {
    setIsLoading(true);

    const successRateParams = fetchSuccessRate();

    const { getSendBatchData } = await sendBatch([{ ...successRateParams, requestUuid: roamSuccessRateUuid }]);

    const successRateRes = getSendBatchData({ requestUuid: roamSuccessRateUuid });

    const isRoamNoData = checkUiDisplayNoData(successRateRes);

    if (isRoamNoData) {
      setSuccessRateNoData(true);
    } else if (successRateRes?.data) {
      parseSuccessRateInfo(successRateRes?.data);
      setSuccessRateNoData(false);
    } else if (['AI Insight subscription required', 'UnityPro subscription required'].includes(successRateRes?.message)) {
      setSuccessRateNoData(true);
    } else {
      setAlertInfo({
        open: true,
        severity: 'error',
        message: successRateRes?.message || 'Error',
      });
      setSuccessRateInfo(cloneDeep(defaultSuccessRateInfo));
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!isAuthReady) return;
    init();
  }, [isAuthReady]);

  return (
    <Box>
      <Card
        sx={{ maxWidth: '100%', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', cursor: successRateInfo?.series.length > 0 && 'pointer' }}
        onClick={() => successRateInfo?.series.length > 0 && setDrawerOpen(true)}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
            <img src={icn_linebar_chart} alt="icn_line_chart" />
            <Typography sx={{ marginLeft: 1, marginRight: 1, color: '#172664', fontWeight: 400, fontSize: '15px' }}>Roaming Success Rate</Typography>
            <CustomTooltip />
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {successRateNoData ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', minHeight: 200 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 400, lineHeight: 1.5, color: '#aaaaaa' }}>No data available</Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'stretch', md: 'stretch' },
                  }}
                >
                  <Box sx={{ width: { xs: '100%', md: 400, marginTop: 8 } }}>
                    <RoamingSuccessRateInfo successRateInfo={successRateInfo} />
                  </Box>

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      mx: 3,
                      borderColor: '#e0e0e0',
                    }}
                  />
                  <Divider
                    sx={{
                      display: { xs: 'block', md: 'none' },
                      my: 2,
                      borderColor: '#e0e0e0',
                    }}
                  />

                  <Box
                    sx={{
                      flex: 1,
                      height: 260,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                    }}
                  >
                    {successRateInfo?.series.length > 0 ? (
                      <SuccessRateBarChart barChartData={successRateInfo?.series} />
                    ) : (
                      <Typography sx={{ fontSize: 14, fontWeight: 400, lineHeight: 1.5, color: '#aaaaaa' }}>No data available</Typography>
                    )}
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {drawerOpen && (
        <DrawerField
          width="580px"
          open={drawerOpen}
          handleClose={() => setDrawerOpen(false)}
          contentKey={`AInsight_Roam_Drawer_${uuidv4()}`}
          commonContent="ainsightRoamDrawer"
          contentProps={{
            auth,
            standalone,
            successRateInfo,
          }}
        />
      )}

      <GlobalAlert open={alertInfo.open} severity={alertInfo.severity} message={alertInfo.message} onClose={() => setAlertInfo(prev => ({ ...prev, open: false }))} />
    </Box>
  );
};

export default Roam;
